import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  confirmSignIn,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { userApi } from '../services/api';

export type UserRole = 'EMPLOYEE' | 'SUPERVISOR' | 'DIRECTOR' | 'BOARD_APPROVER' | 'ADMIN';

export interface AuthenticatedUser {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  department?: string;
  site?: string;
  supervisorId?: string;
  reportsDirectlyToDirector: boolean;
  employeeNumber?: string;
}

export interface LoginResult {
  success: boolean;
  challengeName?: 'NEW_PASSWORD_REQUIRED';
}

interface AuthContextType {
  user: AuthenticatedUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  hasRole: (roles: UserRole | UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserData = async (): Promise<AuthenticatedUser | null> => {
    try {
      const currentUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      const email = attributes.email || '';

      console.log('Cognito user:', currentUser.userId, 'email:', email);

      // Try to fetch full user profile from backend API using email
      // This gets the correct role from DynamoDB
      try {
        // Look up user by email - the backend will use GSI1 to find the user
        const userProfile = await userApi.getUser(email) as {
          userId: string;
          email: string;
          firstName: string;
          lastName: string;
          role: UserRole;
          department?: string;
          site?: string;
          supervisorId?: string;
          reportsDirectlyToDirector?: boolean;
          employeeNumber?: string;
        };

        console.log('User profile from API:', userProfile.userId, 'role:', userProfile.role);

        return {
          userId: userProfile.userId || currentUser.userId,
          email: userProfile.email || email,
          firstName: userProfile.firstName || attributes.given_name || '',
          lastName: userProfile.lastName || attributes.family_name || '',
          role: userProfile.role || 'EMPLOYEE',
          department: userProfile.department,
          site: userProfile.site,
          supervisorId: userProfile.supervisorId,
          reportsDirectlyToDirector: userProfile.reportsDirectlyToDirector || false,
          employeeNumber: userProfile.employeeNumber,
        };
      } catch (apiError: any) {
        // Fallback to Cognito attributes if API fails
        console.error('Could not fetch user profile from API:', apiError.message);
        console.log('Falling back to Cognito attributes - WARNING: This may cause balance lookups to fail');
        return {
          userId: currentUser.userId,
          email: email,
          firstName: attributes.given_name || '',
          lastName: attributes.family_name || '',
          role: (attributes['custom:role'] as UserRole) || 'EMPLOYEE',
          department: attributes['custom:department'],
          site: attributes['custom:site'],
          supervisorId: attributes['custom:supervisorId'],
          reportsDirectlyToDirector: attributes['custom:reportsDirectlyToDirector'] === 'true',
          employeeNumber: attributes['custom:employeeNumber'],
        };
      }
    } catch (error) {
      console.log('User not authenticated');
      return null;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);
      const userData = await fetchUserData();
      setUser(userData);
      setIsLoading(false);
    };

    checkAuth();

    // Listen for auth events
    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          const userData = await fetchUserData();
          setUser(userData);
          break;
        case 'signedOut':
          setUser(null);
          break;
        case 'tokenRefresh':
          const refreshedUser = await fetchUserData();
          setUser(refreshedUser);
          break;
      }
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<LoginResult> => {
    const result = await signIn({
      username: email,
      password,
    });

    console.log('SignIn result:', JSON.stringify(result, null, 2));

    if (result.isSignedIn) {
      const userData = await fetchUserData();
      setUser(userData);
      return { success: true };
    }

    // Check for NEW_PASSWORD_REQUIRED challenge
    const signInStep = result.nextStep?.signInStep;
    console.log('SignIn step:', signInStep);

    if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { success: false, challengeName: 'NEW_PASSWORD_REQUIRED' };
    }

    return { success: false };
  };

  const completeNewPassword = async (newPassword: string): Promise<void> => {
    const result = await confirmSignIn({
      challengeResponse: newPassword,
    });

    if (result.isSignedIn) {
      const userData = await fetchUserData();
      setUser(userData);
    } else {
      throw new Error('Failed to complete password change');
    }
  };

  const logout = async () => {
    await signOut();
    setUser(null);
  };

  const getAccessToken = async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() || null;
    } catch {
      return null;
    }
  };

  const hasRole = (roles: UserRole | UserRole[]): boolean => {
    if (!user) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(user.role);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        completeNewPassword,
        logout,
        getAccessToken,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
